import React, { useCallback, useEffect, useRef, useState } from "react";
// @ts-ignore
import { DSS_URL } from "@/lib/config";
import debounce from "lodash.debounce";
import { FaSearch } from "react-icons/fa";
import {
  IoClose,
  IoCloseSharp,
  IoTimeOutline,
  IoTrendingUp,
} from "react-icons/io5";
import "./style.css";

interface AutocompleteProps {
  value?: string;
  fetchSuggestions: (input: string) => Promise<{
    query: string;
    suggestions: { query: string }[];
  }>;
  onSearch: (input: string) => void;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  value = "",
  fetchSuggestions,
  onSearch,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [trending, setTrending] = useState<string[]>([]);

  const fetchTrending = async () => {
    fetch(`${DSS_URL}/trending`)
      .then((res) => res.json())
      .then((data) => {
        const cleaned = data.suggestions.map((item: any) => item.query);
        setTrending(cleaned);
      });
  };

  const trimSearchesToLatestThree = (): void => {
    const searches = listAllSearches();
    if (searches.length > 5) {
      const latestThreeSearches = searches.slice(-3); // Get the last three items
      localStorage.setItem(
        "recentSearches",
        JSON.stringify(latestThreeSearches)
      );
      setRecentSearches(latestThreeSearches); // Update state to reflect changes
    }
  };

  useEffect(() => {
    fetchTrending();
    trimSearchesToLatestThree();
  }, []);

  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const listAllSearches = (): string[] => {
    const searches = localStorage.getItem("recentSearches");
    return searches ? JSON.parse(searches) : [];
  };

  const removeAllSearches = (): void => {
    localStorage.removeItem("recentSearches");
    setRecentSearches([]); // Update state to reflect changes
  };

  const removeOneSearchAndUpdateState = (searchToRemove: string): void => {
    const updatedSearches = recentSearches.filter(
      (search) => search !== searchToRemove
    );
    localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
    setRecentSearches(updatedSearches); // Update state to reflect changes
  };

  const addRecentSearch = (newSearch: string): void => {
    const updatedSearches = [...recentSearches];
    // Check if the search is already in the list to avoid duplicates
    if (!updatedSearches.includes(newSearch)) {
      updatedSearches.push(newSearch);
      // Keep only the latest three searches
      if (updatedSearches.length > 5) {
        updatedSearches.shift(); // Remove the oldest search
      }
      localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
      setRecentSearches(updatedSearches); // Update state to reflect changes
    }
  };

  useEffect(() => {
    // Load initial state from localStorage
    setRecentSearches(listAllSearches());
  }, []);

  const debouncedFetchSuggestions = useCallback(
    debounce(async (input: string) => {
      setIsLoading(true);
      try {
        const response = await fetchSuggestions(input);
        const fetchedSuggestions = response.suggestions.map(
          (item) => item.query
        );
    
        setSuggestions(fetchedSuggestions);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [fetchSuggestions]
  );

  useEffect(() => {
    if (inputValue && inputValue.length > 2) {
      debouncedFetchSuggestions(inputValue);
    } else {
      setSuggestions([]);
    }
  }, [inputValue, debouncedFetchSuggestions]);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onInputFocus = () => {
    setShowSuggestions(true);
  };

  const onClearInput = () => {
    setInputValue("");
    onSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const onSuggestionClick = (suggestion: string) => {
    onSearch(suggestion);
    setInputValue(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    addRecentSearch(suggestion);
  };

  return (
    <div
      className={`
			autocomplete  sm:z-auto sm:relative
			${showSuggestions ? "" : ""}`}
      ref={wrapperRef}
    >
      <div className="">
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=477168205448449&ev=Search"
        />
        <div
          className={`w-full shadow-md sm:shadow-none flex items-center rounded-none sm:rounded-lg bg-white border border-[#E8E8E8] ${
            showSuggestions ? "z-[998]" : ""
          }`}
        >
          <div
            style={{ display: "flex", flex: 1 }}
            className={`${
              showSuggestions ? "z-[998]" : ""
            } justify-center items-center bg-white sm:rounded-tl-lg sm:rounded-bl-lg`}
          >
            <input
              value={inputValue}
              onChange={onInputChange}
              onFocus={onInputFocus}
              placeholder="What are you looking for today?"
              style={{ outline: "none" }}
              type="text"
              className="bg-white p-4 pr-0 sm:rounded-tl-lg sm:rounded-bl-lg flex flex-1 outline-none focus:outline-none"
              // onBlur={() => setShowSuggestions(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSuggestionClick(inputValue);
              }}
            />

            {inputValue && (
              <button
                className=" bg-gray-500 text-white  hover:bg-[#e12a2d] w-6 h-6  flex justify-center items-center rounded-full"
                onClick={onClearInput}
              >
                <IoClose />
              </button>
            )}
          </div>
          <div
            className={`bg-white p-[7px] pr-2 sm:rounded-tr-lg sm:rounded-br-lg ${
              showSuggestions ? "z-[998]" : ""
            }`}
          >
            <span
              className="flex items-center p-2.5 bg-[#e12a2d] hover:bg-black rounded-full cursor-pointer text-white"
              onClick={() => {
                onSearch(inputValue);
                setShowSuggestions(false);
              }}
            >
              <FaSearch />
            </span>
          </div>
        </div>
      </div>

      {showSuggestions && (
        <>
          <div
            className={`fixed inset-0 bg-black bg-opacity-50 ${
              showSuggestions ? "z-[99]" : ""
            }`}
            onClick={() => setShowSuggestions(false)} // Close overlay when clicked
          ></div>
          <div
            className={`autocomplete-items ${
              showSuggestions ? "z-[998]" : ""
            } rounded-none sm:rounded-lg px-4 bg-red-500  mt-0 sm:mt-2 py-4 ${
              !showSuggestions ? "autocomplete-items-hidden" : ""
            }`}
          >
            {/* {isLoading && <div className="text-center">Loading...</div>} */}
            {trending.length > 0 &&
              suggestions.length === 0 &&
              recentSearches.length > 0 && (
                <div className="w-full">
                  <p className="text-white font-bold mb-2 flex justify-between items-center">
                    <span>Recent Search</span>
                    <span
                      className="font-normal hover:text-black cursor-pointer"
                      onClick={() => removeAllSearches()}
                    >
                      Clear All
                    </span>
                  </p>
                  {recentSearches.map((q, k) => (
                    <div
                      key={k}
                      className="flex w-full justify-between items-center cursor-pointer hover:text-white hover:bg-black rounded-md"
                    >
                      <span
                        className="hover:text-white cursor-pointer text-sm flex items-center gap-2 "
                        onClick={() => onSuggestionClick(q)}
                      >
                        <IoTimeOutline color="white" />
                        <span className="text-gray-200 ">{q}</span>
                      </span>
                      <span
                        className="text-white hover hover:bg-white hover:text-black rounded-md"
                        onClick={() => removeOneSearchAndUpdateState(q)}
                      >
                        <IoCloseSharp />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            {trending.length > 0 && suggestions.length === 0 && (
              <div className="w-full">
                <p className="text-white font-bold mb-2">Trending</p>
                {trending.map((q, k) => (
                  <div
                    key={k}
                    className="flex w-full justify-between items-center cursor-pointer hover:text-white hover:bg-black rounded-md"
                    onClick={() => onSuggestionClick(q)}
                  >
                    <span
                      className="hover:text-white cursor-pointer text-sm flex items-center gap-2 "
                      // onClick={() => onSuggestionClick(q)}
                    >
                      <IoTrendingUp color="white" />
                      <span className="text-gray-200 ">{q}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {suggestions.map((suggestion: any, index) => (
              <div
                className="text-gray-200 cursor-pointer hover:text-white hover:bg-black rounded-md"
                key={index}
                onClick={() => onSuggestionClick(suggestion)}
              >
                <span>{suggestion}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Autocomplete;
